import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import {
  X,
  Plus,
  Trash2,
  Settings,
  Loader2,
  Link as LinkIcon,
  FileText,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "../ui/confirm-dialog";
import crmService from "../../services/crm.service";
import cedenteDocumentoService from "../../services/cedenteDocumento.service";
import { processService } from "../../services/process.service";

export default function CRMMappingsDialog({ 
  entityType, entityId, cedenteId, processoId, status, 
  title, onClose, onMappingDeleted, onStatusChanged 
}) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setLoadingSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [newMapping, setNewMapping] = useState({
    provider: "",
    external_id: "",
    container_id: ""
  });
  const [cedenteDocs, setCedenteDocs] = useState([]);
  const [pipelineStatuses, setPipelineStatuses] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("");
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmResult, setCrmResult] = useState(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const showResult = (type, msg) => {
    setCrmResult({ type, msg });
    setTimeout(() => setCrmResult(null), 4000);
  };

  useEffect(() => {
    crmService.getConfigs()
      .then(data => {
        const active = (data || [])
          .filter(p => p.is_active)
          .map(p => ({ id: p.name, name: p.display_name, icon: p.icon || "🔌" }));
        setProviders(active);
        if (active.length > 0) {
          setNewMapping(prev => ({ ...prev, provider: active[0].id }));
        }
      })
      .catch(() => {});
  }, []);

  const fetchMappings = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      const data = await crmService.getMappings(entityType, entityId);
      setMappings(data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const [crmCardId, setCrmCardId] = useState(null);

  useEffect(() => {
    if (!cedenteId || !entityId) return;
    cedenteDocumentoService.getDocsByCedente(cedenteId, entityId)
      .then(data => {
        setCedenteDocs(data?.docs || []);
        setCrmCardId(data?.crm_card_id || null);
        const statuses = data?.pipeline_statuses || {};
        setPipelineStatuses(statuses);
        const keys = Object.keys(statuses);
        if (keys.length > 0) setSelectedStatus(statuses[keys[0]]);
      })
      .catch(() => {});
  }, [cedenteId, entityId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newMapping.external_id || !newMapping.provider) return;

    try {
      setLoadingSaving(true);
      await crmService.createMapping({
        entity_type: entityType,
        entity_id: entityId,
        ...newMapping
      });
      setNewMapping(prev => ({ provider: prev.provider, external_id: "", container_id: "" }));
      fetchMappings();
    } catch (error) {
      alert(error.response?.data?.detail || "Erro ao criar mapeamento");
    } finally {
      setLoadingSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover este vinculo com CRM?")) return;
    try {
      await crmService.deleteMapping(id);
      fetchMappings();
      onMappingDeleted?.();
    } catch {
    }
  };

  const noProviders = !loading && providers.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-muted/50">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-bold text-foreground">Conexoes CRM</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full transition-colors">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Vinculos Ativos</p>
            {loading ? (
              <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
            ) : mappings.length === 0 ? (
              <div className="py-6 text-center border-2 border-dashed border-gray-100 rounded-lg">
                <p className="text-sm text-muted-foreground">Nenhum CRM vinculado a este {title}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {mappings.map((m) => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-muted rounded-lg border border-gray-100 group">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{providers.find(p => p.id === m.provider)?.icon || "🔌"}</span>
                      <div>
                        <p className="text-xs font-bold text-muted-foreground uppercase">{m.provider}</p>
                        {m.provider === "clickup" ? (
                          <a
                            href={`https://app.clickup.com/t/${m.external_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-mono text-blue-600 hover:underline"
                          >
                            {m.external_id}
                          </a>
                        ) : (
                          <p className="text-sm font-mono text-blue-600">{m.external_id}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="p-2 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {noProviders ? (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-amber-600 bg-statusWarningBg border border-statusWarning/20 rounded-lg px-4 py-3">
                Nenhum CRM configurado. Configure em Admin &rarr; CRM.
              </p>
            </div>
          ) : (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Novo Vinculo</p>
              <form onSubmit={handleCreate} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-bold text-muted-foreground uppercase mb-1">Provider</label>
                    <select
                      className="w-full h-9 px-3 text-sm bg-card border border-border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                      value={newMapping.provider}
                      onChange={(e) => setNewMapping({...newMapping, provider: e.target.value})}
                    >
                      {providers.map(p => <option key={p.id} value={p.id}>{p.icon} {p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-muted-foreground uppercase mb-1">ID Externo</label>
                    <input
                      type="text"
                      className="w-full h-9 px-3 text-sm border border-border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                      placeholder="ex: 86azrj123"
                      value={newMapping.external_id}
                      onChange={(e) => setNewMapping({...newMapping, external_id: e.target.value})}
                    />
                  </div>
                </div>
                <Button type="submit" disabled={saving || !newMapping.external_id || !newMapping.provider} className="w-full gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Vincular CRM
                </Button>
              </form>
            </div>
          )}
        </div>

        {cedenteId && entityId && (
          <div className="px-6 pb-4 pt-0 border-t border-gray-100">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 pt-4">Task no CRM</p>
            <div className="flex flex-col gap-2">
              <div className="text-xs text-muted-foreground">
                {cedenteDocs.length > 0
                  ? <span>{cedenteDocs.length} documento(s) — </span>
                  : <span>Sem documentos — </span>
                }
                {crmCardId ? (
                  <a
                    href={`https://app.clickup.com/t/${crmCardId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-mono"
                  >
                    {crmCardId}
                  </a>
                ) : (
                  <span className="text-amber-600">sem task vinculada</span>
                )}
              </div>
              {!crmCardId ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={crmLoading}
                  className="gap-2 text-xs"
                  onClick={async () => {
                    setCrmLoading(true);
                    try {
                      const res = await cedenteDocumentoService.criarCardCrm(cedenteId, entityId);
                      if (res.success) {
                        showResult("success", `Task criada: ${res.crm_card_id}`);
                        setCrmCardId(res.crm_card_id);
                      } else {
                        showResult("error", res.message || "Falha ao criar task");
                      }
                    } catch {
                      showResult("error", "Erro ao criar task no CRM");
                    } finally {
                      setCrmLoading(false);
                    }
                  }}
                >
                  {crmLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                  Criar task no CRM
                </Button>
              ) : cedenteDocs.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {Object.keys(pipelineStatuses).length > 0 && (
                    <div>
                      <label className="block text-[10px] font-bold text-muted-foreground uppercase mb-1">Mover para status</label>
                      <select
                        className="w-full h-8 px-2 text-xs bg-card border border-border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                        value={selectedStatus}
                        onChange={e => setSelectedStatus(e.target.value)}
                      >
                        <option value="">— só enviar, sem mover —</option>
                        {Object.entries(pipelineStatuses).map(([k, v]) => (
                          <option key={k} value={v}>{k} — {v}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={crmLoading}
                    className="gap-2 text-xs"
                    onClick={async () => {
                      setCrmLoading(true);
                      try {
                        const res = await cedenteDocumentoService.enviarDocsCrm(cedenteId, entityId, selectedStatus || null);
                        if (res.success) {
                          showResult("success", "Documentos enviados ao CRM");

                          // Pós envio: se estiver em análise, perguntar se deseja remover
                          if (status === "analise" && processoId) {
                            setShowStatusConfirm(true);
                          }
                        } else {
                          showResult("error", res.message || "Falha ao enviar documentos");
                        }
                      } catch {
                        showResult("error", "Erro ao enviar documentos");
                      } finally {
                        setCrmLoading(false);
                      }
                    }}
                  >
                    {crmLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Enviar docs ao CRM
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {crmResult && (
          <div className={`mx-6 mb-3 px-4 py-2 rounded-lg text-xs font-medium ${crmResult.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-statusErrorBg text-statusError border border-statusError/20"}`}>
            {crmResult.msg}
          </div>
        )}

        <div className="px-6 py-3 bg-statusInfoBg text-[10px] text-blue-600 flex items-center gap-2">
          <LinkIcon className="h-3 w-3" />
          <span>Este vinculo automatiza a troca de status no CRM ao assinar documentos.</span>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showStatusConfirm}
        onOpenChange={setShowStatusConfirm}
        title="Remover de Análise?"
        description="Os documentos foram enviados com sucesso ao CRM. Deseja remover o substituído do status 'Em Análise'?"
        confirmText="Sim, remover"
        cancelText="Manter em análise"
        onConfirm={async () => {
          setIsUpdatingStatus(true);
          try {
            await processService.linkCedente(processoId, {
                cedente_id: cedenteId,
                status: "pendente",
            });
            onStatusChanged?.();
            setShowStatusConfirm(false);
            toast.success("Status atualizado!");
          } catch (err) {
            toast.error("Erro ao atualizar status");
          } finally {
            setIsUpdatingStatus(false);
          }
        }}
        isLoading={isUpdatingStatus}
        icon="help"
      />
    </div>
  );
}
